import { FirebaseError } from '@firebase/util';
import { kebabCase } from 'lodash';
import { useCallback, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../data/context/app-context';
import { FormMessage, FormMessageType } from '../../../data/utils/form.utils';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../elements/button/button';
import { InputFieldController } from '../../../elements/form/controllers/input-field';
import { FormRow } from '../../../elements/form/form';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import { Spinner } from '../../../elements/spinner/spinner';
import { useForgotPasswordForm } from './form/use-forgot-password-form';

export function ForgotPassword() {
  const intl = useIntl();

  const { validating, forgotPassword } = useAppContext();

  const { form, formIsValid } = useForgotPasswordForm();

  const [loading, setLoading] = useState<boolean>(false);

  const [formMessage, setFormMessage] = useState<FormMessage | null>(null);

  const onHandleSubmit = useCallback(async () => {
    form.handleSubmit(async () => {
      const values = form.getValues();

      setLoading(true);

      setFormMessage(null);

      try {
        const result = await forgotPassword(values.email);

        if (!result.success) {
          setFormMessage({
            type: FormMessageType.ERROR,
            message: intl.formatMessage({
              id: `common.errors.${kebabCase(
                result.message ?? 'unknown-error'
              )}`,
              defaultMessage: result.message ?? 'unknown-error',
            }),
          });
        } else {
          setFormMessage({
            type: FormMessageType.SUCCESS,
            message: intl.formatMessage({ id: 'auth.forgot-password.success' }),
          });
        }
      } catch (error) {
        if (error instanceof FirebaseError) {
          setFormMessage({
            type: FormMessageType.ERROR,
            message: intl.formatMessage({
              id: `common.errors.${kebabCase(error.code ?? 'unknown-error')}`,
              defaultMessage: error.code ?? 'unknown-error',
            }),
          });
        } else {
          setFormMessage({
            type: FormMessageType.ERROR,
            message: (error as Error).message,
          });
        }
      }

      setLoading(false);
    })();
  }, [intl, form, forgotPassword]);

  return validating ? (
    <Spinner />
  ) : (
    <>
      <h2>
        <CustomFormattedMessage id="auth.forgot-password.title" />
      </h2>
      <p>
        <CustomFormattedMessage id="auth.forgot-password.content" />
      </p>

      {formMessage?.type === FormMessageType.SUCCESS ? (
        <p className="form-success">{formMessage.message}</p>
      ) : (
        <>
          {formMessage?.type === FormMessageType.ERROR && (
            <p className="form-error">{formMessage.message}</p>
          )}

          <FormProvider {...form}>
            <form onSubmit={onHandleSubmit}>
              <FormRow>
                <InputFieldController
                  name="email"
                  type="email"
                  label={<CustomFormattedMessage id="common.email" />}
                  placeholder={intl.formatMessage({ id: 'common.email' })}
                />
              </FormRow>

              <FormRow>
                <p>
                  <Button
                    useAsSubmit
                    type={ButtonType.SECONDARY}
                    icon={loading ? ButtonIcon.LOADING : ButtonIcon.NONE}
                    disabled={!formIsValid || loading}
                    onClick={onHandleSubmit}
                  >
                    <CustomFormattedMessage id="auth.forgot-password.button" />
                  </Button>
                </p>
              </FormRow>
            </form>
          </FormProvider>
        </>
      )}
    </>
  );
}
